import { useEffect, useState, useMemo, useCallback } from 'react';
import { Event } from 'nostr-tools';
import { useAdmin } from '../contexts/AdminContext';
import { getNostrPool } from '../lib/nostrPool';
import { RELAYS } from '../config/constants';
import { useGlobalModeration } from './useGlobalModeration';

export const useNostrUserEvents = (pubkey: string | null, allowGlobal: boolean = false) => {
    const [events, setEvents] = useState<Event[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLinking, setIsLinking] = useState(false); // For pagination loading
    const pool = getNostrPool(); // Use singleton
    const { settings } = useAdmin();
    const { blockedEventIds, blockedPubkeys } = useGlobalModeration(); // Global Moderation (We only care about hidden events here)

    // Memoize isolateCommunity to prevent re-subscription on unrelated setting changes
    const isolateCommunity = useMemo(() => settings.isolateCommunity && !allowGlobal, [settings.isolateCommunity, allowGlobal]);

    const loadMore = useCallback(async () => {
        if (!pubkey || isLinking || isLoading) return;

        const oldestEvent = events[events.length - 1];
        if (!oldestEvent) return;

        setIsLinking(true);

        const filter: any = {
            kinds: [1],
            authors: [pubkey],
            until: oldestEvent.created_at - 1,
            limit: 20
        };

        if (isolateCommunity) {
            filter['#t'] = ['mavililer'];
        }

        const moreEvents: Event[] = [];
        const sub = pool.subscribeMany(RELAYS, filter, {
            onevent(event: Event) {
                if (isolateCommunity) {
                    const tags = event.tags.filter(t => t[0] === 't').map(t => t[1]);
                    if (!tags.includes('mavililer')) return;
                }
                moreEvents.push(event);
            },
            oneose() {
                if (moreEvents.length > 0) {
                    setEvents(prev => [...prev, ...moreEvents].sort((a, b) => b.created_at - a.created_at));
                }
                setIsLinking(false);
                sub.close();
            }
        });

        // Safety timeout for pagination too
        setTimeout(() => {
            setIsLinking(false);
            sub.close();
        }, 5000);
    }, [pubkey, events, isLinking, isLoading, isolateCommunity, pool]);


    useEffect(() => {
        if (!pubkey) return;

        setIsLoading(true);
        setEvents([]);

        const filter: any = {
            kinds: [1],
            authors: [pubkey],
            limit: 20
        };

        if (isolateCommunity) {
            filter['#t'] = ['mavililer'];
        }

        // Safety timeout: stop loading after 5 seconds even if relays are slow
        const timeout = setTimeout(() => {
            setIsLoading(false);
        }, 5000);

        const sub = pool.subscribeMany(RELAYS, filter, {
            onevent(event: Event) {
                clearTimeout(timeout); // We got at least one event (optional: could keep timeout for EOSE)
                // If isolateCommunity is on, double check that it has 'mavililer' tag
                if (isolateCommunity) {
                    const tags = event.tags.filter(t => t[0] === 't').map(t => t[1]);
                    if (!tags.includes('mavililer')) return;
                }

                setEvents((prev) => {
                    const exists = prev.some((e) => e.id === event.id);
                    if (exists) return prev;
                    return [...prev, event].sort((a, b) => b.created_at - a.created_at);
                });
            },
            oneose() {
                clearTimeout(timeout);
                setIsLoading(false);
            },
        });

        return () => {
            clearTimeout(timeout);
            sub.close();
        };
    }, [pubkey, isolateCommunity]);

    // REACTIVE FILTERING PIPELINE
    const filteredEvents = useMemo(() => {
        return events.filter(e => {
            // Global Admin Moderation Filter
            if (blockedEventIds.has(e.id)) return false;
            if (blockedPubkeys.has(e.pubkey)) return false;
            return true;
        });
    }, [events, blockedEventIds, blockedPubkeys]);

    return { events: filteredEvents, isLoading, loadMore, isLinking };
};
